/**
 * Pure display helpers for the Life OS pages: codes, dates, money, ages and
 * small list rules. Everything that reads a clock takes `now`/`today` as an
 * argument so it is unit-testable; every calendar date is America/New_York
 * (spec §0), whatever the browser's zone.
 */

export const HOME_TZ = 'America/New_York';

// --- Codes ----------------------------------------------------------------------------

export const taskCode = (n: number): string => `#${n}`;
export const approvalCode = (n: number): string => `A${n}`;
export const runCode = (n: number): string => `R${n}`;

// --- Calendar dates (YYYY-MM-DD, America/New_York) ---------------------------------------

const ymdFmt = new Intl.DateTimeFormat('en-CA', { timeZone: HOME_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

/** The America/New_York calendar date of an instant, as 'YYYY-MM-DD'. */
export function ymdInHomeZone(at: Date): string {
  return ymdFmt.format(at);
}

function ymdToUtc(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Whole days from `from` to `to` (both 'YYYY-MM-DD'); negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((ymdToUtc(to) - ymdToUtc(from)) / 86_400_000);
}

/** 'YYYY-MM-DD' shifted by `n` days. */
export function addDays(ymd: string, n: number): string {
  return new Date(ymdToUtc(ymd) + n * 86_400_000).toISOString().slice(0, 10);
}

/** The Monday after `ymd` (never `ymd` itself). */
export function nextMonday(ymd: string): string {
  const dow = new Date(ymdToUtc(ymd)).getUTCDay(); // 0 = Sun
  return addDays(ymd, ((8 - dow) % 7) || 7);
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'Oct 12', with the year when it differs from `today`'s. */
export function shortDate(ymd: string, today?: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const base = `${MONTH[(m ?? 1) - 1]} ${d}`;
  return today && today.slice(0, 4) !== String(y) ? `${base}, ${y}` : base;
}

/** 'Sat Oct 3'. */
export function dayLabel(ymd: string): string {
  const dow = new Date(ymdToUtc(ymd)).getUTCDay();
  return `${WEEKDAY[dow]} ${shortDate(ymd)}`;
}

export type DueTone = 'overdue' | 'today' | 'soon' | 'later';

/**
 * The due chip on a task row. Red ('overdue') only when the date has passed;
 * 'Today'/'Tomorrow', a weekday inside the week, then a short date.
 */
export function dueChip(dueOn: string | null, today: string): { label: string; tone: DueTone } | null {
  if (!dueOn) return null;
  const n = daysBetween(today, dueOn);
  if (n < 0) {
    const label = n === -1 ? 'Yesterday' : n >= -30 ? `${-n}d late` : shortDate(dueOn, today);
    return { label, tone: 'overdue' };
  }
  if (n === 0) return { label: 'Today', tone: 'today' };
  if (n === 1) return { label: 'Tomorrow', tone: 'soon' };
  if (n < 7) return { label: WEEKDAY[new Date(ymdToUtc(dueOn)).getUTCDay()] ?? dueOn, tone: 'soon' };
  return { label: shortDate(dueOn, today), tone: 'later' };
}

/** Days-left wording for upcoming items: 'today', 'in 3d', '2d late' (the due chip's word). */
export function daysLeftLabel(daysLeft: number): string {
  if (daysLeft === 0) return 'today';
  if (daysLeft === 1) return 'tomorrow';
  if (daysLeft < 0) return `${-daysLeft}d late`;
  return `in ${daysLeft}d`;
}

/** Trip date range: 'Oct 12 – 15', 'Oct 30 – Nov 2', or 'No dates yet'. */
export function tripDates(t: { startsOn: string | null; endsOn: string | null }, today: string): string {
  if (!t.startsOn) return 'No dates yet';
  const start = shortDate(t.startsOn, today);
  if (!t.endsOn || t.endsOn === t.startsOn) return start;
  const sameMonth = t.startsOn.slice(0, 7) === t.endsOn.slice(0, 7);
  return `${start} – ${sameMonth ? Number(t.endsOn.slice(8)) : shortDate(t.endsOn, today)}`;
}

// --- Times and ages ------------------------------------------------------------------------

const timeFmt = new Intl.DateTimeFormat('en-US', { timeZone: HOME_TZ, hour: 'numeric', minute: '2-digit' });
const stampFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: HOME_TZ,
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/** '2:30 PM' in America/New_York. */
export function clock(iso: string): string {
  return timeFmt.format(new Date(iso));
}

/** 'Oct 3, 2:30 PM' in America/New_York. */
export function stamp(iso: string): string {
  return stampFmt.format(new Date(iso));
}

/** Like `stamp`, plus the year when it isn't `today`'s ('Jan 1, 2027, 5:21 AM'). */
export function stampWithYear(iso: string, today: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const ymd = ymdInHomeZone(d);
  return `${shortDate(ymd, today)}, ${clock(iso)}`;
}

/** Minutes of sleep → '6h 32m'. */
export function sleepLabel(min: number): string {
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** 'HH:MM' (24h) → '2:00 PM'; anything else is returned unchanged. */
export function to12h(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  if (h > 23) return hhmm;
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

/** A compact age: '45m', '3h', '2d', '3w', '5mo'. A day or more is never hours ('26h' reads as '1d'). */
export function ageLabel(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) return '—';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 24) return `${Math.floor(hours)}h`;
  const days = hours / 24;
  if (days < 14) return `${Math.floor(days)}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

/** Age of an ISO instant relative to `now`, e.g. '3d ago'; null-safe; a future instant (clock skew) is 'just now'. */
export function since(iso: string | null, now: Date): string {
  if (!iso) return 'never';
  const hours = (now.getTime() - new Date(iso).getTime()) / 3_600_000;
  if (!Number.isFinite(hours)) return 'never';
  return hours < 1 / 60 ? 'just now' : `${ageLabel(hours)} ago`;
}

/** Minutes → '15m', '1h 30m'. */
export function minutesLabel(min: number | null): string | null {
  if (min === null || !Number.isFinite(min) || min <= 0) return null;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// --- Money ------------------------------------------------------------------------------

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const usdWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** Integer cents → '$1,234.50'; whole dollars drop the '.00'. */
export function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—';
  return cents % 100 === 0 ? usdWhole.format(cents / 100) : usd.format(cents / 100);
}

/**
 * A user-typed dollar amount → integer cents, parsed as a string so '1.005'
 * never becomes 100.4999… (the Finance rule in CLAUDE.md). Null when blank or
 * not a plain non-negative amount.
 */
export function parseDollarsToCents(raw: string): number | null {
  const s = raw.replace(/[$,\s]/g, '');
  const m = /^(\d+)(?:\.(\d{0,2}))?$/.exec(s);
  if (!m) return null;
  const whole = Number(m[1]);
  const frac = Number(((m[2] ?? '') + '00').slice(0, 2));
  return whole * 100 + frac;
}

/** Cents → the plain string an amount input shows ('12.5' → '12.50'). */
export function centsToInput(cents: number | null): string {
  if (cents === null) return '';
  return (cents / 100).toFixed(2);
}

/** Monthly-equivalent cost of a service; usage/one-time/free count as 0. */
export function monthlyCents(s: { costCents: number | null; cadence: string; status: string }): number {
  if (s.status === 'cancelled' || s.costCents === null) return 0;
  if (s.cadence === 'monthly') return s.costCents;
  if (s.cadence === 'yearly') return Math.round(s.costCents / 12);
  return 0;
}

// --- Birthdays ----------------------------------------------------------------------------

/** Days from `today` to the next occurrence of a birthday (0 = today). Feb 29 counts as Mar 1 in other years. */
export function daysToBirthday(b: { month: number; day: number }, today: string): number {
  const y = Number(today.slice(0, 4));
  const at = (year: number): string => {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const [m, d] = b.month === 2 && b.day === 29 && !leap ? [3, 1] : [b.month, b.day];
    return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };
  const n = daysBetween(today, at(y));
  return n >= 0 ? n : daysBetween(today, at(y + 1));
}

/** 'Oct 12', plus ' · turns 34' when the year is known and the next birthday is ahead. */
export function birthdayLabel(b: { month: number; day: number; year: number | null }, today: string): string {
  const base = `${MONTH[b.month - 1] ?? '?'} ${b.day}`;
  if (b.year === null) return base;
  const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
  const passed = b.month < tm || (b.month === tm && b.day < td);
  const turns = ty - b.year + (passed ? 1 : 0);
  return turns >= 2 ? `${base} · turns ${turns}` : base;
}

// --- Lists ------------------------------------------------------------------------------------

/** The ADHD list rule: show the first `n`, count the rest. */
export function collapse<T>(items: readonly T[], n = 3): { shown: T[]; hidden: number } {
  return { shown: items.slice(0, n), hidden: Math.max(0, items.length - n) };
}

/** Pluralise a count: plural(2, 'task') → '2 tasks'. */
export function plural(n: number, word: string, many = `${word}s`): string {
  return `${n} ${n === 1 ? word : many}`;
}

// --- Labels -------------------------------------------------------------------------------------

export const TASK_STATUS_LABEL: Record<string, string> = {
  inbox: 'To sort',
  next: 'Next',
  waiting: 'Waiting',
  someday: 'Someday',
  done: 'Done',
  dropped: 'Dropped',
};

export const AGENT_LEVEL_LABEL: readonly string[] = [
  'Off — never runs',
  'Draft — you approve the plan and the ship',
  'Build — you approve the ship',
  'Ship — runs unless a hard stop',
  'Full — runs and reports',
];

/** A level's short name: 1 → 'Draft'. */
export function levelName(level: number): string {
  return AGENT_LEVEL_LABEL[level]?.split(' — ')[0] ?? `L${level}`;
}

/** 'L1 · Draft', for selects and facts. */
export function levelLabel(level: number): string {
  return `L${level} · ${levelName(level)}`;
}

/**
 * What an audit action means, in words (Connections → Activity). The raw key
 * stays available as a tooltip; an unknown key falls back to readable words.
 */
export const AUDIT_LABEL: Record<string, string> = {
  'oauth.code_reuse': 'Login code reused — blocked',
  'oauth.refresh_reuse': 'Old refresh token reused — app signed out',
  'oauth.consent_granted': 'App allowed in',
  'oauth.consent_denied': 'App turned away',
  'oauth.client_registered': 'App registered',
  'mcp.auth_failed': 'Rejected a bad token',
  'auth.bearer_denied': 'Rejected a token',
  'csrf.rejected': 'Blocked a cross-site request',
  'client.revoked': 'App disconnected',
  'token.created': 'Token created',
  'token.revoked': 'Token revoked',
  'approval.decide': 'Approval decided',
  'approval.expired': 'Approval expired',
  'run.created': 'Agent run started',
  'run.transition': 'Agent run moved on',
  'run.resumed': 'Agent run resumed',
  'policy.change': 'Autonomy level changed',
  'project.protected_update': 'Protected project setting changed',
  'signal.done': 'Signal: marked done',
  'signal.snooze': 'Signal: snoozed',
  'signal.move': 'Signal: moved to tomorrow',
  'signal.drop': 'Signal: dropped',
  'signal.someday': 'Signal: moved to Someday',
  'signal.undo': 'Signal: undo',
  'signal.cap': 'Signal: daily limit reached',
};

export function auditLabel(action: string): string {
  const known = AUDIT_LABEL[action];
  if (known) return known;
  const tool = /^tool\.(.+)$/.exec(action);
  if (tool) return `Used ${humanize(tool[1]!).toLowerCase()}`;
  return humanize(action.replace(/\./g, ' '));
}

/** 'agent_plan' → 'Plan', 'awaiting_ship_approval' → 'Awaiting ship approval'. */
export function humanize(key: string): string {
  const s = key.replace(/^agent_/, '').replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The first line of free text, trimmed to `max` characters. */
export function firstLine(text: string | null | undefined, max = 120): string {
  const line = (text ?? '').trim().split(/\r?\n/, 1)[0] ?? '';
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}

// --- <input type="datetime-local"> round trip (browser-local wall time) -------------------

const pad = (n: number): string => String(n).padStart(2, '0');

/** ISO instant → 'YYYY-MM-DDTHH:MM' in the browser's zone, for a datetime-local input. */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A datetime-local value → ISO instant; null when blank or unparsable. */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * A raw sender handle made readable: '+15716830001' → '(571) 683-0001', an
 * iMessage group id ('chat433415784994934050') → 'Group chat'. Anything else
 * (an email address, a name) comes back unchanged.
 */
export function prettyHandle(handle: string): string;
export function prettyHandle(handle: string | null | undefined): string | null;
export function prettyHandle(handle: string | null | undefined): string | null {
  if (handle === null || handle === undefined) return null;
  const h = handle.trim();
  if (/^chat\d{6,}$/i.test(h)) return 'Group chat';
  if (/^\+?[\d\s().-]{7,}$/.test(h)) {
    const digits = h.replace(/\D/g, '');
    const us = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.length === 10 && !h.startsWith('+') ? digits : null;
    if (us) return `(${us.slice(0, 3)}) ${us.slice(3, 6)}-${us.slice(6)}`;
  }
  return handle;
}
